import React, { useEffect, useState } from "react";
import { Card, Button, Spin, message } from "antd";
import { useParams } from "react-router-dom";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { ITop10Article, ITop10ArticleItem } from "../../../types/Top10Article";
import { getTop10ArticleByID, sortArticleSortedItems } from "../action";

type Props = { fetchData: () => void };

const SortedItemsSorter: React.FC<Props> = ({ fetchData }) => {
  const { id } = useParams();
  const [sortedItems, setSortedItems] = useState<ITop10ArticleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Fetch initial article
  useEffect(() => {
    if (!id) return;
    const fetchArticle = async () => {
      try {
        setInitialLoading(true);
        const article = await getTop10ArticleByID(id);
        if (article?.SortedItems) {
          setSortedItems(article.SortedItems);
        }
      } catch (err: any) {
        message.error(err.message || "Failed to load article");
      } finally {
        setInitialLoading(false);
      }
    };
    fetchArticle();
  }, [id]);

  // Drag and drop handler
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const reordered = Array.from(sortedItems);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    setSortedItems(reordered);
  };

  // Save new order
  const handleSave = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const newOrder = sortedItems.map((item) => {
        if (item.Type === "TEXT" && item.RichText?.id) {
          return { Type: item.Type, RichText: item.RichText.id };
        }
        return { Type: item.Type }; // TOP10 items won't have RichText key
      });

      await sortArticleSortedItems(id, newOrder);
      message.success("Sorted items updated successfully!");
      fetchData();
    } catch (err: any) {
      message.error(err.message || "Failed to save sorted items");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card title="Sort Article Items">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="sortedItems">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                minHeight: 200,
                border: "1px dashed #ccc",
                padding: 8,
                borderRadius: 6,
              }}
            >
              {sortedItems.map((item, index) => (
                <Draggable
                  key={index}
                  draggableId={index.toString()}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        padding: "8px 12px",
                        marginBottom: 8,
                        background: snapshot.isDragging ? "#e6f7ff" : "#fafafa",
                        border: "1px solid #ddd",
                        borderRadius: 6,
                        ...provided.draggableProps.style,
                      }}
                    >
                      {item.Type === "TOP10"
                        ? "Top10 Placeholder"
                        : item.RichText?.Title.En ||
                          item.RichText?.Title.Ar ||
                          "Untitled"}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Button
        type="primary"
        block
        style={{ marginTop: 16 }}
        onClick={handleSave}
        disabled={sortedItems.length === 0}
      >
        {loading ? <Spin /> : "Save Order"}
      </Button>
    </Card>
  );
};

export default SortedItemsSorter;
